import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { useI18n } from 'builder/hooks/useI18n'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { SnackbarTypes, actions as uiActions } from 'builder/modules/ui'
import { selectors as userSelectors, actions } from 'builder/modules/user'
import ModalOverlay from '../ModalOverlay'
import Spinner from '../Spinner'
import Button, { ButtonTheme } from '../Button'
import { useRestartSubscription } from './hooks/useRestartSubscription'
import {
  Body,
  ButtonContainer,
  CloseIcon,
  Container,
  DateText,
  HeaderContainer,
  Title,
} from './styles'

export const RestartSubscriptionModal = () => {
  const dispatch = useDispatch()
  const { i18n } = useI18n()
  const i18nPrefix = 'builder.restart_subscription'
  const navigate = useNavigate()
  const user = useTypedSelector(userSelectors.userData)
  const { mutateAsync, isLoading } = useRestartSubscription()
  const subscriptionLastDate = new Date(user?.billingInfo.premiumEndsAt || '')

  const handleModalClose = useCallback(() => {
    dispatch(uiActions.closeRestartSubscriptionModal())
  }, [dispatch])

  const openSnackBar = useCallback(
    (snackBarText: string, success: boolean) => {
      setTimeout(() => {
        dispatch(
          uiActions.setSnackBarOpen({
            status: true,
            type: success ? SnackbarTypes.success : SnackbarTypes.failure,
            text: snackBarText,
          }),
        )
      }, 200)

      setTimeout(() => {
        dispatch(uiActions.closeSnackBar())
      }, 5000)
    },
    [dispatch],
  )

  const handleRestart = async () => {
    try {
      const response = await mutateAsync()

      if (response.data && response.data.success) {
        dispatch(actions.setAccount(response.data))
        openSnackBar(i18n.t(`${i18nPrefix}.done_message`), true)
        navigate('/account')
        handleModalClose()
      } else {
        openSnackBar(i18n.t(`${i18nPrefix}.failed_message`), false)
        navigate('/account')
        handleModalClose()
      }
    } catch (error) {
      console.error(error)
      openSnackBar(i18n.t(`${i18nPrefix}.failed_message`), false)
      navigate('/account')
      handleModalClose()
    }
  }

  const dateFormat = i18n.t(`${i18nPrefix}.dialog_message_date_format`)
  const dateTranslated = format(subscriptionLastDate, dateFormat)
  const bodyText = i18n.t(`${i18nPrefix}.dialog_message`)

  return (
    <ModalOverlay
      fullScreen={true}
      overlayFadeDuration={250}
      contentSlideDuration={0}
      onClick={handleModalClose}
    >
      {isLoading ? (
        <Spinner />
      ) : (
        <Container>
          <HeaderContainer>
            <Title>{i18n.t(`${i18nPrefix}.dialog_title`)}</Title>
            <CloseIcon onClick={handleModalClose} />
          </HeaderContainer>

          <Body>
            {bodyText}
            <DateText>{dateTranslated}</DateText>
          </Body>
          <ButtonContainer>
            <Button onClick={handleModalClose} theme={ButtonTheme.ghost}>
              {i18n.t(`${i18nPrefix}.no_button`)}
            </Button>
            <Button onClick={handleRestart} disabled={isLoading}>
              {i18n.t(`${i18nPrefix}.yes_button`)}
            </Button>
          </ButtonContainer>
        </Container>
      )}
    </ModalOverlay>
  )
}
